// src/hooks/user/useFollowStatus.js
import { useQuery, useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { getFollowStatus } from "../../utils/follow/followApi";
import {
  FOLLOW_CACHE_CONFIG,
  FOLLOW_RETRY_CONFIG,
  followQueryKeys,
  followPerformanceUtils,
  followCacheUtils,
} from "../../utils/follow/queryConfig";

/**
 * Custom hook for checking follow status between current user and target user
 *
 * Implements optimized caching with React Query to reduce API calls and improve performance.
 * Includes performance monitoring and intelligent retry logic.
 * Requirements: 6.1, 6.2, 6.4, 8.1, 8.2
 *
 * @param {string} userId - The ID of the user to check follow status for
 * @param {Object} options - Query options
 * @param {boolean} options.enabled - Whether the query should be enabled (default: true)
 * @param {boolean} options.prefetchProfile - Whether to prefetch user profile data (default: false)
 * @returns {Object} Query result with follow status data
 */
export const useFollowStatus = (userId, options = {}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { enabled = true, prefetchProfile = false, ...queryOptions } = options;
  const queryClient = useQueryClient();

  const query = useQuery(
    followQueryKeys.followStatus(userId),
    async () => {
      const startTime = Date.now();

      try {
        const token = await getAccessTokenSilently();
        const result = await getFollowStatus(userId, token);

        followPerformanceUtils.trackQueryPerformance(
          followQueryKeys.followStatus(userId),
          startTime
        );

        return result;
      } catch (error) {
        followPerformanceUtils.trackQueryPerformance(
          followQueryKeys.followStatus(userId),
          startTime
        );
        throw error;
      }
    },
    {
      enabled:
        enabled && !!getAccessTokenSilently && !!userId && userId !== user?.sub,
      ...FOLLOW_CACHE_CONFIG.followStatus,
      ...FOLLOW_RETRY_CONFIG.standard,
      ...queryOptions,
    }
  );

  // Handle prefetching in useEffect to avoid hook violations
  useEffect(() => {
    if (prefetchProfile && query.data && getAccessTokenSilently) {
      followCacheUtils
        .prefetchUserData(queryClient, userId, getAccessTokenSilently)
        .catch((error) => {
          // Silently fail prefetch to not affect main functionality
          console.debug("Failed to prefetch user data:", error);
        });
    }
  }, [
    prefetchProfile,
    query.data,
    queryClient,
    userId,
    getAccessTokenSilently,
  ]);

  return query;
};
